/**
 * This file holds actions. The actions commit mutations and can perform asynchronous operations.
 * Please refer to https://vuex.vuejs.org/guide/actions.html for more information
 */
import Vue from 'vue';
import http from "@/http";
import { fabric } from "@vfdp-customs/customs-fabric";
import * as types from "./types";
import axios from "axios";
import returnUrlParams from "@/http/returnUrlParams";
import { sessionKeys } from '@/data/constants';
import store from "@/store"

const actions = {
  [types.UPLOAD](context, { inputFile }) {
    const data = new FormData();
    const tmallEnabled = context.rootGetters['utils/tmallEnabled'];

    const requestUrl = `${context.rootState.endPoints.customizer.ugc}`;

    let isMy = false;
    if (typeof my !== 'undefined') {
      isMy = true;
    }
    if (tmallEnabled && isMy) {
      const filename = `image.${inputFile.split(',')[0].split(':')[1].split(';')[0].split('/')[1]}`;
      const blob = dataURItoBlob(inputFile);
      data.append('file-input', blob, filename);
    }
    else {
      data.append("file-input", inputFile);
    }

    return new Promise((resolve, reject) => {
      http
        .post(requestUrl, data)
        .then(({ data }) => {
          if(context.rootState.utils.configuration.ugc.enableUGCContactDetails) {
            context
              .dispatch(types.UGC_GATEWAY, {
                image_id: data.temp_image_id
              })
              .then(({data}) => {
                resolve(data);
              })
              .catch(reject);
          }
          resolve(data);

        })
        .catch(reject);
    });
  },
  [types.GET_UGC_SWATCH](
    context,
    { productName, size, componentName, placements, endpointsType = 'customizer' }
  ) {
    const data = new FormData();
    data.append("productName", productName);
    data.append("size", size);
    data.append("componentName", componentName);
    placements.forEach((el, i) => {
      Object.keys(el).forEach(prop => {
        data.append(`placements[${i}][${prop}]`, el[prop]);
      });
    });
    return http.post(context.rootState.endPoints[endpointsType].ugcSwatch, data);
  },
  [types.SAVE_UGC_SWATCH]({ state, rootState, rootGetters}, payload) {
    const activeUGCIndex = state.tempData.activeUGCIndex;
    const componentName = state.tempData.features[activeUGCIndex].activeUGCComponent;
    const uploadedAnother = rootGetters['ugc/tempData/uploadedAnotherImage'];

    this.commit("ugc/tempData/ADD_UGC_COMPONENT", {
      componentName,
      defaultUGCSwatchId: rootState.recipe.defaultRecipe.components[componentName].swatch_id,
      uploadedAnother,
    });

    state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.size = state.tempData.features[activeUGCIndex].ugc_size;
    const { background_color } = state.tempData.features[activeUGCIndex];
    if (background_color) {
      state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.background_color = background_color;
    }

    state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.ugc_swatch = payload;
    state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.placements = [
      ...this.state.ugc.placements.responses[componentName]
    ];

    const tonalStitch = state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.placements.filter(e => e && e.tonal_stitch_color);
    if (tonalStitch && tonalStitch.length) {
      state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.tonal_stitch_color = tonalStitch[0].tonal_stitch_color;
    }

    const { image_url = false } = state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.original_image;
    if (!image_url || (image_url !== state.tempData.features[activeUGCIndex].ugc_image && !state.tempData.features[activeUGCIndex].ugc_image.includes("placement"))) {
      state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.original_image = {
        image_url: state.tempData.features[activeUGCIndex].ugc_image,
        image_id: state.tempData.features[activeUGCIndex].ugc_image_id
      };
    }

    const queryParams = rootState.utils.queryParams;
    const secondaryImage = queryParams.secondaryImage || queryParams.cbtImage;
    if(queryParams.ugcImage && secondaryImage) {
      state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.original_image = {
        image_url: `${rootState.endPoints.customizer.ugcImageFolder}${queryParams.ugcImage}.${queryParams.ugcImageType}`,
        image_id: queryParams.ugcImage
      };
    }

    if (state.stateManager.isPopArtOrTileApplied) {
      state.tempData.features[activeUGCIndex].recipe.components[componentName].ugc.filter_applied = state.stateManager.isPopArtOrTileApplied;
    }
  },
  [types.GET_ASSETS_3D](context, { productName, size, componentName, multipart }) {
    const params = { productName, size, componentName };
    if (multipart > 1) {
      params.multipart = multipart;
    }
    return http
      .get(context.rootState.endPoints.customizer.getAssets3d, { params })
      .then(data => context.commit(types.SET_ASSETS_3D, data.data));
  },
  [types.SAVE_ASSETS_3D]({ dispatch, state, getters, rootState }, component = null) {
    return new Promise((resolve, reject) => {
      const componentName = component || state.tempData.features[state.tempData.activeUGCIndex].activeUGCComponent;
      const pdm = rootState.utils.pdm;
      // size, multipart and component name cannot be hardcoded
      dispatch("GET_ASSETS_3D", {
          componentName,
          productName: pdm.sku,
          size: state.tempData.features[state.tempData.activeUGCIndex].ugc_size,
          multipart: getters["tempData/checkMultipart"]
        })
        .then(resolve)
        .catch(reject);
    });
  },
  [types.SET_CANVAS_IMAGE](context, { fabricCanvas, imageSrc, isSecondPlacement }) {
    return new Promise(resolve => {
      // checks if image address is an absolute URL and, if not, prefixes it with s3Url
      const imgUrl = (imageSrc.search(/^https?:\/\//i) === 0 || imageSrc.search("data:image") === 0) ? imageSrc : `${context.rootState.endPoints.customizer.s3Url}${imageSrc}`;
      let imageResizeRatio = 0.8;
      new fabric.Image.fromURL(
        imgUrl,
        img => {
          const imageOptions = {
            originX: "center",
            originY: "center",
            borderColor: "#666",
            cornerStrokeColor: "#666",
            cornerColor: "#ffffff",
            transparentCorners: false,
            borderScaleFactor: 1.5,
            lockScalingFlip: true,
            lockUniScaling: true
          };
          if (context.state.backgroundImage) {
            if (isSecondPlacement) {
              imageResizeRatio = 1.01;
              imageOptions.scaleX = context.state.backgroundImage.scaleX * imageResizeRatio;
              imageOptions.scaleY = context.state.backgroundImage.scaleY * imageResizeRatio;
            }

            fabricCanvas.remove(context.state.backgroundImage);
          }

          const canvasImage = fabricCanvas.getObjects("image");
          if (canvasImage.length) {
            canvasImage.forEach(img => fabricCanvas.remove(img));
          }

          // resizes the image to fit the canvas according to the
          // side with the lowest ratio between canvas and image size
          let canvasSize;
          let resizedImg;
          const canvasWidth = fabricCanvas.getWidth(),
            canvasHeight = fabricCanvas.getHeight(),
            imageWidth = img.width,
            imageHeight = img.height;
          const widthRatio = canvasWidth / imageWidth,
            heightRatio = canvasHeight / imageHeight;
          if (isSecondPlacement) {
            resizedImg = img.set(imageOptions);
          } else if (widthRatio < heightRatio) {
            canvasSize = canvasWidth * imageResizeRatio;
            resizedImg = img.scaleToWidth(canvasSize).set(imageOptions);
          } else {
            canvasSize = canvasHeight * imageResizeRatio;
            resizedImg = img.scaleToHeight(canvasSize).set(imageOptions);
          }
          context.commit("tempData/SET_SCALE_X", resizedImg.get("scaleX"));

          if (fabricCanvas.overlayImage && !isSecondPlacement) {
            const scale = Vue.prototype.$ugc.getImageScale(fabricCanvas.overlayImage, resizedImg);
            resizedImg = img.scale(scale);
          }

          if(context.state.stateManager.activeControl &&
             context.state.stateManager.activeControl !== 'ugc-replace-image-control')
          {
            const extraPlacement = fabricCanvas.getObjects().find(item => item.type === "path");
            fabricCanvas.remove(extraPlacement);
          }

          fabricCanvas
            .add(resizedImg)
            .centerObject(resizedImg)
            .setActiveObject(resizedImg);
          context.state.backgroundImage = resizedImg;
          resolve(resizedImg);
        },
        { crossOrigin: "anonymous" }
      );
    });
  },
  [types.LOCK_FABRIC_FEATURES](context, { fabricCanvas, lockFeatures }) {
    const canvas = fabricCanvas.getObjects()[0];
    canvas.lockRotation = lockFeatures;
    canvas.lockScalingX = lockFeatures;
    canvas.lockScalingY = lockFeatures;
    canvas.hasControls = !lockFeatures;
  },
  [types.CREATE_UGC_FLOW_VIEWS](context) {
    const { features, activeUGCIndex } = context.rootState.ugc.tempData;
    const activeUGCComponent = features[activeUGCIndex].activeUGCComponent;

    const ugcFlowViews = [];
    const ugcComponents = context.getters["getUGCEnabledComponents"];
    const isGCC = context.rootGetters["utils/isGCC"];

    ugcComponents.splice(ugcComponents.indexOf(activeUGCComponent), 1);
    ugcComponents.unshift(activeUGCComponent);

    let index = 0;
    const ugcFlowComponents = {};
    let component;
    for (let i = 0; i < ugcComponents.length; i++) {
      component = ugcComponents[i];
      for (
        let j = 0;
        j < (context.rootState.utils.pdm.components[component]?.ugc?.placements?.length || 0);
        j++
      ) {
        ugcFlowViews.push("ugc-design-view");
        ugcFlowComponents[index] = component;
        index++;
      }
      if (!isGCC) {
        if (i < ugcComponents.length - 1) {
          let almostDoneComponent = context.rootState.utils.configuration?.modals?.ugcAlmostDone ? "new-ugc-almost-done" : "ugc-almost-done"
          ugcFlowViews.push(almostDoneComponent);
          index++;
        }
      }
    }

    const { enableNewConfirmDesignComponent = true } = context.rootState.utils.configuration;
    const { enableUgcConfirmDesignStep } = context.rootState.utils.configuration.modals;

    if (enableUgcConfirmDesignStep) ugcFlowViews.push("ugc-confirm-design-step");
    else if (enableNewConfirmDesignComponent) ugcFlowViews.push("ugc-new-confirm-design");
    else ugcFlowViews.push("ugc-confirm-design");

    context.commit("tempData/SET_UGC_FLOW_VIEWS", ugcFlowViews);
    context.commit("tempData/SET_UGC_FLOW_COMPONENTS", ugcFlowComponents);
  },
  [types.UGC_GATEWAY](context, { image_id }) {
    const contactDetails = context.rootState.UGCEntryPoint.contactDetails;
    const requestUrl = context.rootState.endPoints.customizer.ugcGateway;
    const optIn = context.rootState.UGCEntryPoint.optIn || !!window.$cookies.get(sessionKeys.ugcOptIn);
    const data = {
      "image_id": image_id,
      "first_name": contactDetails.firstName,
      "last_name":contactDetails.lastName,
      "email": contactDetails.emailAddress,
      "opt_in": optIn
    };
    return new Promise((resolve, reject) => {
        axios({
            url: requestUrl,
            method: "POST",
            headers: { "content-type": "application/json" },
            data: JSON.stringify(data)
        })
        .then(({ data }) => {
            resolve(data);
        })
        .catch(reject);
    });
  }
};

export default actions;

const dataURItoBlob = dataURI => {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;

  if (dataURI.split(',')[0].indexOf('base64') >= 0)
      byteString = atob(dataURI.split(',')[1]);
  else
      byteString = unescape(dataURI.split(',')[1]);
  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], {type:mimeString});
}

